import {
  Badge,
  Button,
  Card,
  FileInput,
  Group,
  PasswordInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  useAddUser,
  useConfig,
  useEditUser,
  useFloor,
  useFloorAccept,
  useFloorImport,
  useNotifyTest,
  usePutConfig,
  useReprocess,
  useUsers,
} from "../../api/generated/stockvol";
import type { ConfigOut, FloorOut, UserOut } from "../../api/generated/model";
import { useSiteInfo } from "../../api/hooks";
import { useAuth } from "../../auth";
import { act, confirm } from "../../lib/actions";
import { formatLocal, localToUtcIso, toLocalInput } from "../../lib/time";
import { IconMail, IconMap2, IconRefresh, IconSettings, IconUsers } from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";

const data = <T,>(r: unknown) => (r as { data: T }).data;

/** Admin (M6.6): reprocess, the site config, the floor, users, a test mail. */
export default function AdminPage() {
  return (
    <Stack gap="lg">
      <PageHeader title="Admin" subtitle="Reprocessing, the site configuration, the pad floor, users and mail" />
      <Tabs defaultValue="reprocess" keepMounted={false} variant="pills" radius="md">
        <Tabs.List>
          <Tabs.Tab value="reprocess" leftSection={<IconRefresh size={16} />}>
            Reprocess
          </Tabs.Tab>
          <Tabs.Tab value="config" leftSection={<IconSettings size={16} />}>
            Site config
          </Tabs.Tab>
          <Tabs.Tab value="floor" leftSection={<IconMap2 size={16} />}>
            Floor
          </Tabs.Tab>
          <Tabs.Tab value="users" leftSection={<IconUsers size={16} />}>
            Users
          </Tabs.Tab>
          <Tabs.Tab value="mail" leftSection={<IconMail size={16} />}>
            Mail
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="reprocess" pt="md">
          <Card>
            <Reprocess />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="config" pt="md">
          <Card>
            <Config />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="floor" pt="md">
          <Card>
            <Floor />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="users" pt="md">
          <Card>
            <Users />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="mail" pt="md">
          <Card>
            <Mail />
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function Reprocess() {
  const tz = useSiteInfo().data?.timezone ?? "UTC";
  const qc = useQueryClient();
  const m = useReprocess();
  const [from, setFrom] = useState(() => toLocalInput(Date.now() - 86_400_000, tz));
  const [to, setTo] = useState(() => toLocalInput(Date.now(), tz));
  return (
    <Stack maw={560}>
      <Text size="sm" c="dimmed">
        Every complete or partial sweep that started in the period is processed again with the active configuration and
        floor; the new results replace the current ones as they finish.
      </Text>
      <Group grow>
        <TextInput type="datetime-local" label={`From (${tz})`} value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
        <TextInput type="datetime-local" label="To" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
      </Group>
      <Group>
        <Button
          loading={m.isPending}
          onClick={() =>
            confirm("Reprocess", "Queue every sweep of this period for processing again?", () =>
              void act(
                async () => {
                  const r = await m.mutateAsync({ data: { t_from: localToUtcIso(from, tz), t_to: localToUtcIso(to, tz) } });
                  return r;
                },
                { done: "Sweeps queued", qc, refresh: ["runs"] },
              ),
            )
          }
        >
          Reprocess
        </Button>
      </Group>
    </Stack>
  );
}

function Config() {
  const qc = useQueryClient();
  const cfg = useConfig({ query: { select: data<ConfigOut> } });
  const put = usePutConfig();
  const [yaml, setYaml] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (cfg.data?.yaml_text && yaml === "") setYaml(cfg.data.yaml_text);
  }, [cfg.data, yaml]);
  return (
    <Stack>
      <Text size="sm">
        Active: <code>{cfg.data?.sha256.slice(0, 12) ?? "—"}</code>{" "}
        {cfg.data?.activated_at ? `since ${formatLocal(cfg.data.activated_at, "UTC")} UTC` : ""}
      </Text>
      <Textarea
        value={yaml}
        onChange={(e) => setYaml(e.currentTarget.value)}
        autosize
        minRows={12}
        maxRows={30}
        styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
        data-testid="config-yaml"
      />
      <Group align="flex-end">
        <TextInput label="Why" value={note} onChange={(e) => setNote(e.currentTarget.value)} w={360} />
        <Button
          loading={put.isPending}
          onClick={() =>
            confirm(
              "Apply the configuration",
              "The worker uses it from the next run on. An invalid file is refused and the active one stays.",
              () => void act(() => put.mutateAsync({ data: { yaml, note: note || null } }), { done: "Configuration applied", qc, refresh: ["config"] }),
            )
          }
        >
          Apply
        </Button>
      </Group>
    </Stack>
  );
}

function Floor() {
  const qc = useQueryClient();
  const floors = useFloor(undefined, { query: { select: data<FloorOut[]> } });
  const imp = useFloorImport();
  const accept = useFloorAccept();
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<string | null>("candidate");
  return (
    <Stack>
      <Table withTableBorder fz="xs" maw={720}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Version</Table.Th>
            <Table.Th>Source</Table.Th>
            <Table.Th>Observed</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Accepted by</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {floors.data?.map((f) => (
            <Table.Tr key={f.id}>
              <Table.Td>
                v{f.version} {f.is_active && <Badge size="xs">in force</Badge>}
              </Table.Td>
              <Table.Td>{f.source}</Table.Td>
              <Table.Td>{f.observed_frac === null ? "—" : `${Math.round(100 * f.observed_frac)} %`}</Table.Td>
              <Table.Td>{formatLocal(f.created_at, "UTC")} UTC</Table.Td>
              <Table.Td>{f.accepted_by ?? ""}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <SimpleGrid cols={{ base: 1, sm: 2 }} maw={720}>
        <Stack gap="xs">
          <FileInput label="Import a surveyed floor (GeoTIFF)" accept=".tif,.tiff" value={file} onChange={setFile} />
          <Button
            variant="light"
            disabled={!file}
            loading={imp.isPending}
            onClick={() =>
              file &&
              confirm("Import the floor", `Make ${file.name} the next floor version?`, () =>
                void act(() => imp.mutateAsync({ data: { file } }), { done: "Floor imported", qc, refresh: ["floor"] }),
              )
            }
          >
            Import
          </Button>
        </Stack>
        <Stack gap="xs">
          <Select
            label="Accept from the floor model"
            data={[
              { value: "candidate", label: "the candidate (falls only)" },
              { value: "observed", label: "the latest observed floor" },
            ]}
            value={mode}
            onChange={setMode}
          />
          <Button
            variant="light"
            loading={accept.isPending}
            onClick={() =>
              confirm("Accept a new floor", "The volumes of the next runs are measured against it.", () =>
                void act(
                  () => accept.mutateAsync({ data: { mode: (mode ?? "candidate") as "candidate" | "observed" } }),
                  { done: "Floor accepted", qc, refresh: ["floor"] },
                ),
              )
            }
          >
            Accept
          </Button>
        </Stack>
      </SimpleGrid>
    </Stack>
  );
}

function Users() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const users = useUsers({ query: { select: data<UserOut[]> } });
  const add = useAddUser();
  const edit = useEditUser();
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [role, setRole] = useState<string | null>("viewer");
  const roles = ["viewer", "operator", "admin"];
  return (
    <Stack maw={760}>
      <Table withTableBorder fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>User</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Active</Table.Th>
            <Table.Th>Last login</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {users.data?.map((u) => (
            <Table.Tr key={u.id}>
              <Table.Td>{u.username}</Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  data={roles}
                  value={u.role}
                  disabled={u.username === me?.username}
                  onChange={(v) =>
                    v &&
                    confirm("Change the role", `${u.username} becomes ${v}?`, () =>
                      void act(() => edit.mutateAsync({ userId: u.id, data: { role: v as "viewer" | "operator" | "admin" } }), {
                        done: "Role changed",
                        qc,
                        refresh: ["users"],
                      }),
                    )
                  }
                  w={120}
                />
              </Table.Td>
              <Table.Td>
                <Switch
                  checked={u.is_active}
                  disabled={u.username === me?.username}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    confirm(on ? "Enable" : "Disable", `${on ? "Enable" : "Disable"} ${u.username}?`, () =>
                      void act(() => edit.mutateAsync({ userId: u.id, data: { is_active: on } }), {
                        done: on ? "Enabled" : "Disabled",
                        qc,
                        refresh: ["users"],
                      }),
                    );
                  }}
                />
              </Table.Td>
              <Table.Td>{u.last_login_at ? formatLocal(u.last_login_at, "UTC") + " UTC" : "never"}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Group align="flex-end">
        <TextInput label="New user" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <PasswordInput label="Password (8+)" value={pw} onChange={(e) => setPw(e.currentTarget.value)} />
        <Select label="Role" data={roles} value={role} onChange={setRole} w={130} />
        <Button
          loading={add.isPending}
          disabled={name.length < 2 || pw.length < 8}
          onClick={() =>
            void act(
              () =>
                add.mutateAsync({
                  data: { username: name, password: pw, role: (role ?? "viewer") as "viewer" | "operator" | "admin" },
                }),
              { done: `${name} added`, qc, refresh: ["users"] },
            ).then(() => {
              setName("");
              setPw("");
            })
          }
        >
          Add
        </Button>
      </Group>
    </Stack>
  );
}

function Mail() {
  const m = useNotifyTest();
  return (
    <Stack maw={560}>
      <Text size="sm" c="dimmed">
        Sends a test mail to the configured recipients. Without an SMTP server configured it says so.
      </Text>
      <Group>
        <Button variant="light" loading={m.isPending} onClick={() => void act(() => m.mutateAsync(), { done: "Test mail sent" })}>
          Send a test mail
        </Button>
      </Group>
    </Stack>
  );
}
